import { Link } from "react-router-dom";
import FooterRouteLinks from "./FooterRouteLinks";
import { useState } from "react";
import {
  Package, Mail, MapPin, Globe, ShieldCheck, Users,
  ArrowRight, Headphones, CheckCircle2, Send, Instagram,
  Facebook, Youtube
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Footer = () => {
  const { t } = useTranslation();
  const { localePath } = useLocalizedPath();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Subscription is owned by a SECURITY DEFINER routine: the client can no
      // longer write (or re-activate) subscriber rows by raw email match.
      const { data, error } = await supabase.rpc("newsletter_subscribe" as any, {
        p_email: email,
        p_source: "footer",
      });
      if (error) throw error;

      const result = (data ?? {}) as { ok?: boolean; error?: string };
      if (!result.ok) {
        if (result.error === "rate_limited") {
          throw new Error(t('footer.newsletterRateLimited', 'Too many attempts. Please try again later.'));
        }
        throw new Error(t('footer.newsletterInvalidEmail', 'Please enter a valid email address.'));
      }

      toast({ title: t('footer.newsletterSuccess', 'Thanks for subscribing!'), description: t('footer.newsletterSuccessDesc', 'You\'ll receive our latest updates.') });
      setEmail("");
    } catch (err: any) {
      console.error('Newsletter subscribe error:', err);
      toast({ title: t('footer.newsletterError', 'Could not subscribe'), description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative text-white" style={{ background: "var(--color-navy)" }}>
      {/* Newsletter CTA Band */}
      <div className="border-b border-white/10">
        <div className="container py-10 md:py-14">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 sm:gap-8">
            <div className="space-y-2 max-w-md">
              <h3 className="text-lg md:text-2xl font-display font-bold text-white">
                {t('footer.newsletterTitle', 'Stay in the loop')}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                {t('footer.newsletterDesc', 'Get route alerts, special rates, and platform updates delivered to your inbox.')}
              </p>
            </div>
            <form onSubmit={handleNewsletter} className="flex w-full lg:w-auto gap-0">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder', 'Enter your email')}
                className="flex-1 lg:w-72 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder:text-white/70 text-sm focus:outline-none focus:border-brand-orange transition-colors duration-brand-fast min-text-16"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-3 border-2 border-brand-orange bg-brand-navy text-brand-orange hover:bg-brand-navy/90 font-medium text-sm transition-colors duration-brand-fast flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">{isSubmitting ? t('footer.subscribing', 'Subscribing...') : t('footer.subscribe', 'Subscribe')}</span>
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container py-14 md:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-12">

          {/* Brand Section */}
          <div className="col-span-2 space-y-5">
            <Link to={localePath("/")} className="inline-flex items-center group">
              <img src="/logo-small.png" alt="EuroCollis" className="h-10 w-auto brightness-0 invert" width="40" height="40" loading="lazy" />
            </Link>
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-orange font-semibold">
              {t('footer.tagline')}
            </p>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm">
              {t('footer.description')}
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-3 pt-2">
              {[
                { icon: Instagram, href: "https://www.instagram.com/eurocollis?igsh=MXhzaXkzMjgzZGljZQ==", label: "Instagram" },
                { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61589413442390", label: "Facebook" },
                { icon: () => <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>, href: "https://x.com/eurocollis?s=21", label: "X" },
                { icon: Youtube, href: "https://youtube.com/@eurocollis?si=rw0B8g8kAV5hAIBQ", label: "YouTube" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href !== "#" ? "_blank" : undefined}
                  rel={href !== "#" ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  className="flex items-center justify-center h-9 w-9 bg-white/5 border border-white/10 hover:bg-brand-orange hover:border-brand-orange text-white/70 hover:text-white transition-all duration-brand-fast"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-3">
              <div className="flex items-center gap-1.5 text-white/70 text-xs">
                <ShieldCheck className="h-3.5 w-3.5 text-brand-info" />
                <span>{t('footer.verified')}</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/70 text-xs">
                <CheckCircle2 className="h-3.5 w-3.5 text-brand-success" />
                <span>{t('footer.secure')}</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/70 text-xs">
                <Headphones className="h-3.5 w-3.5 text-brand-orange" />
                <span>{t('footer.support247')}</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-xs uppercase tracking-[0.15em] font-semibold text-white/70 mb-5">
              {t('footer.services')}
            </h4>
            <ul className="space-y-1.5">
              {[
                { to: "/quote", label: t('footer.getQuote') },
                { to: "/how-it-works", label: t('footer.howItWorks') },
                { to: "/shipping-routes", label: t('footer.shippingRoutes', 'Shipping Routes') },
                { to: "/track", label: t('footer.trackBooking', 'Track Booking') },
                 { to: "/for-couriers", label: t('footer.becomeCourier') },
                { to: "/custom-request", label: t('footer.customRequest') },
                { to: "/affiliates", label: t('footer.affiliates', 'Affiliates') },
                { to: "/support", label: t('footer.support') },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={localePath(to)}
                    className="text-white/70 hover:text-white text-sm transition-colors duration-brand-fast hover:translate-x-0.5 rtl:hover:-translate-x-0.5 inline-block"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="space-y-4">
            <h4 className="text-xs uppercase tracking-[0.15em] font-semibold text-white/70 mb-5">
              {t('footer.company')}
            </h4>
            <ul className="space-y-1.5">
              {[
                { to: "/about", label: t('footer.aboutUs') },
                { to: "/faq", label: t('footer.faq') },
                { to: "/policy-centre", label: t('footer.policyCentre') },
                { to: "/press", label: t('footer.press', 'Press') },
                { to: "/partnerships", label: t('footer.partnerships', 'Partnerships') },
                { to: "/benmira-foundation", label: t('footer.benmiraFoundation', 'Benmira Foundation') },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={localePath(to)}
                    className="text-white/70 hover:text-white text-sm transition-colors duration-brand-fast hover:translate-x-0.5 rtl:hover:-translate-x-0.5 inline-block"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 lg:col-span-1 space-y-4">
            <h4 className="text-xs uppercase tracking-[0.15em] font-semibold text-white/70 mb-5">
              {t('footer.getInTouch')}
            </h4>
            <div className="space-y-4">
              <a href="mailto:support@eurocollis.com" className="flex items-center gap-3 text-white/70 hover:text-white text-sm transition-colors duration-brand-fast">
                <Mail className="h-4 w-4 text-brand-info shrink-0" />
                support@eurocollis.com
              </a>
              <div className="flex items-center gap-3 text-white/70 text-sm">
                <MapPin className="h-4 w-4 text-brand-warning shrink-0" />
                {t('footer.servingRoute')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Route Links Grid */}
      <FooterRouteLinks />

      {/* Bottom Bar */}
      <div className="border-t border-white/[0.06]">
        <div className="container py-5">
          <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row sm:justify-between sm:gap-6">
            <p className="text-white/30 text-[11px] sm:text-xs text-center sm:text-left sm:order-1">
              © {new Date().getFullYear()} EuroCollis. {t('footer.allRightsReserved')}
            </p>
            <div className="w-full text-center text-[11px] leading-none whitespace-nowrap sm:order-2 sm:w-auto sm:text-xs">
              <Link to={localePath("/policy-centre")} className="inline text-white/40 sm:hover:text-white/70 transition-colors duration-brand-fast align-baseline">
                {t('footer.policyCentre')}
              </Link>
              <span aria-hidden="true" className="text-white/30">&nbsp;-&nbsp;</span>
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("eurocollis:open-cookie-settings"));
                }}
                className="inline appearance-none p-0 m-0 bg-transparent border-0 font-normal text-[inherit] leading-none text-white/40 sm:hover:text-white/70 transition-colors duration-brand-fast align-baseline"
              >
                {t('footer.cookieSettings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
